/**
 * @fileoverview The archive page (/plants/archive): every plant taken out of the garden,
 * what it still keeps, and two ways out of here, Restore and Delete forever.
 *
 * Its own page as well as a status filter on the garden, because this is
 * where a gardener comes looking for a plant that went missing, and deleting
 * for good belongs somewhere deliberate rather than on a card's menu.
 *
 * Every failure is said in words: a restore in a toast as well as above the
 * list (which may be scrolled out of sight), a delete in its dialog, and an
 * archive that could not be read instead of "Nothing archived". A failure on
 * our side (a 500 or no answer) says the standard sentence instead, reported
 * to Sentry, with the support address as a link and no "Try again" (D31,
 * lib/serverFailure).
 *
 * Where: the route /plants/archive, inside PlantsProvider; reached from the
 * sort and filter sheet's archive link.
 * API: GET /plants?status=0 (useArchivedPlants, query
 * ['archived-plants', userId]); Restore through PlantsContext.restorePlant
 * (PATCH /plants/:id/status); DELETE /plants/:id { confirm: 'DELETE' }
 * (deletePlantForever).
 * Settings: none.
 * Words (en/es): plants.archive.* (title, intro, backToGarden, loading,
 * loadFailed, empty, kept, restore, restoring, restoreFailed, delete.open,
 * delete.failed), plants.form.deleteForeverEllipsis; DeletePlantDialog's own.
 *
 * @module ArchivedPlants
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Sprout, MapPin, Trash2, Undo2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { useArchivedPlants } from '@/shared/hooks/useArchivedPlants';
import { usePlantsContext } from '@/shared/hooks/usePlantsContext';
import { Button } from '@/shared/components/ui/button';
import { FramedPhoto } from '@/shared/components/ui/FramedPhoto';
import { useQueryClient } from '@tanstack/react-query';
import { deletePlantForever } from '@/shared/api/plantCatalogue';
import { DeletePlantDialog } from './DeletePlantDialog';
import { GrowLoader } from '@/shared/components/ui/GrowLoader';
import { useToast } from '@/shared/components/ui/use-toast';
import { errorMessage } from '@/shared/lib/errorMessage';
import { FailureText } from '@/shared/components/ServerFailure';
import type { Plant } from '@/shared/types/plant';
import { plantName } from '@/shared/lib/plantName';

/**
 * Plants taken out of the garden, and the way back.
 *
 * Removing a plant archives it rather than deleting it, which is only a
 * promise worth making if the gardener can see what was archived and undo it.
 * This is that page.
 */
export const ArchivedPlants: React.FC = () => {
  const { reactTranslate: t } = useTranslation();
  /**
   * A count in words, "1 photo" or "3 photos", with the delete dialog's keys.
   *
   * @param count - How many.
   * @param one - The key for exactly one (plants.archive.delete.photoOne).
   * @param many - The key for any other count, with {count}.
   * @returns The words.
   */
  const countWords = (count: number, one: 'photoOne' | 'noteOne', many: 'photos' | 'notes'): string =>
    (count === 1 ? t(`plants.archive.delete.${one}`) : t(`plants.archive.delete.${many}`, { count }));
  const queryClient = useQueryClient();
  const { restorePlant } = usePlantsContext();
  const { toast } = useToast();

  /*
   * The plant being removed for good, if any.
   *
   * Held here rather than per row so only one dialog exists at a time: a
   * confirmation opened over the wrong plant is the failure this whole
   * screen is guarding against.
   */
  const [deleting, setDeleting] = React.useState<Plant | null>(null);
  /** True while the delete is on its way; the dialog cannot be closed meanwhile. */
  const [deleteBusy, setDeleteBusy] = React.useState(false);
  /** Why the last delete failed, said in the dialog. */
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  /** Deletes the plant in the dialog for good, keeping the dialog open with the error if it fails. */
  const handleDelete = async (): Promise<void> => {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deletePlantForever(deleting.plant_obj_id);
      // The archive is a filtered view of the same list the garden reads,
      // and this page lists it from its own query, which has to hear too or
      // the deleted plant stays on the page.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['plants'] }),
        queryClient.invalidateQueries({ queryKey: ['archived-plants'] }),
      ]);
      setDeleting(null);
    } catch (err) {
      // The server's refusal where it gave one, never axios's "Request
      // failed with status code 500".
      setDeleteError(errorMessage(err, t('plants.archive.delete.failed')));
    } finally {
      setDeleteBusy(false);
    }
  };
  /** Whose archive; nothing is read until the account is known. */

  /** The plant whose Restore is on its way, so its buttons cannot be pressed twice. */
  const [restoringId, setRestoringId] = React.useState<number | null>(null);
  /** Why the last restore failed, shown above the list. */
  const [error, setError] = React.useState<string | null>(null);

  /** The archived plants (GET /plants?status=0), with their photos and notes. */
  const { data: plants = [], isLoading, isError, error: loadError, refetch } = useArchivedPlants();

  /**
   * Puts a plant back in the garden, saying so if it cannot: above the list,
   * and in a toast, because the row pressed may be far below the top.
   *
   * @param plantObjId - The plant.
   */
  const handleRestore = async (plantObjId: number): Promise<void> => {
    setRestoringId(plantObjId);
    setError(null);
    try {
      // Through the context, which brings the garden, the archive and the
      // outlook back in step together. Doing it here meant remembering the
      // list, and the outlook was the one forgotten: a restored plant
      // reappeared carrying the advice for a garden it had not been in.
      const result = await restorePlant(plantObjId);
      if (!result.success) {
        // The server's own sentence when it refused, else ours.
        const message = result.error || t('plants.archive.restoreFailed');
        setError(message);
        toast({ title: message, variant: 'destructive' });
        return;
      }
      await refetch();
    } catch (err) {
      // The context reports rather than throws, so this is a surprise; it is
      // still said, in the same two places.
      const message = errorMessage(err, t('plants.archive.restoreFailed'));
      setError(message);
      toast({ title: message, variant: 'destructive' });
    } finally {
      setRestoringId(null);
    }
  };

  /**
   * The plant's first photo for its thumbnail, or '' for the sprout placeholder.
   *
   * @param plant - The archived plant.
   */
  const photoFor = (plant: Plant): string => {
    const photo = plant.photos?.[0];
    return photo?.signed_photo_url || photo?.url || '';
  };

  return (
    <div className="container mx-auto max-w-3xl px-4 py-6">
      <Link
        to="/plants"
        className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        {t('plants.archive.backToGarden')}
      </Link>

      <h1 className="text-2xl font-semibold text-foreground font-body">
        {t('plants.archive.title')}
      </h1>
      <p className="mt-1 text-sm text-muted-foreground">{t('plants.archive.intro')}</p>

      {error && (
        <p className="mt-4 text-sm text-destructive"><FailureText text={error} /></p>
      )}

      {/* A failed read is not an empty archive. Once read, a later failed
          refetch keeps the list it has. */}
      {isError && plants.length === 0 ? (
        <p role="alert" className="py-10 text-sm text-destructive"><FailureText text={errorMessage(loadError, t('plants.archive.loadFailed'))} /></p>
      ) : isLoading ? (
        <GrowLoader variant="block" label={t('plants.archive.loading')} className="py-10" />
      ) : plants.length === 0 ? (
        <p className="py-10 text-sm text-muted-foreground">{t('plants.archive.empty')}</p>
      ) : (
        <ul className="mt-5 space-y-2">
          {plants.map((plant) => {
            const thumb = photoFor(plant);
            const busy = restoringId === plant.plant_obj_id;
            return (
              <li
                key={plant.plant_obj_id}
                className="flex flex-wrap items-center gap-3 rounded-lg border border-border bg-card p-3"
              >
                {thumb ? (
                  <span className="relative block h-12 w-12 shrink-0 overflow-hidden rounded-md bg-muted data-[small=true]:bg-transparent">
                    <FramedPhoto src={thumb} alt="" loading="lazy" />
                  </span>
                ) : (
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-md bg-muted">
                    <Sprout className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                  </div>
                )}

                <div className="min-w-0 flex-1 basis-40">
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                    <span className="font-medium">{plantName(plant)}</span>
                    {plant.location?.name && (
                      <span className="flex shrink-0 items-center gap-0.5 text-xs text-muted-foreground">
                        <MapPin className="h-3 w-3 shrink-0" aria-hidden="true" />
                        {plant.location.name}
                      </span>
                    )}
                  </div>
                  {/* What it keeps is the reason archiving is not deleting, so
                      it is worth stating on the row rather than assumed. */}
                  <p className="text-xs text-muted-foreground">
                    {/* "1 photo", not "1 photos": the counts in the delete dialog's own words. */}
                    {t('plants.archive.kept', {
                      photos: countWords(plant.photos?.length ?? 0, 'photoOne', 'photos'),
                      notes: countWords(plant.notes?.length ?? 0, 'noteOne', 'notes'),
                    })}
                  </p>
                </div>

                {/* Under the plant on a phone, where beside it the buttons ran off the card. */}
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0 sm:flex-nowrap">
                  <Button
                    variant="outline"
                    onClick={() => handleRestore(plant.plant_obj_id)}
                    disabled={busy}
                    className={cn('gap-1.5')}
                  >
                    <Undo2 className="h-4 w-4" aria-hidden="true" />
                    {busy ? t('plants.archive.restoring') : t('plants.archive.restore')}
                  </Button>
                  {/* Named, beside Restore. An unlabelled grey icon here was the
                      only way to delete a plant, and nobody found it. */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setDeleteError(null); setDeleting(plant); }}
                    disabled={busy}
                    aria-label={t('plants.archive.delete.open', { name: plantName(plant) })}
                    className="gap-1.5 text-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" aria-hidden="true" />
                    {t('plants.form.deleteForeverEllipsis')}
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>

      )}
      {deleting && (
        <DeletePlantDialog
          isOpen
          onOpenChange={(open) => { if (!open) setDeleting(null); }}
          plantName={plantName(deleting)}
          photos={deleting.photos?.length ?? 0}
          notes={deleting.notes?.length ?? 0}
          busy={deleteBusy}
          error={deleteError}
          onConfirm={() => { void handleDelete(); }}
        />
      )}
    </div>
  );
};
